import React from "react";
import { motion } from "framer-motion";
import { Card, CardFooter, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Repeat2 } from "lucide-react";
import { PostType } from "@/types/post";
import useCompactMode from "@/hooks/useCompactMode";
import CompactPost from "./CompactPost";
import { FeedAd } from "../ads/FeedAd";

interface PostWrapperProps {
  post: PostType | 'ad';
  isDeleted?: boolean;
  children?: React.ReactNode;
  onAvatarClick?: (e: React.MouseEvent) => void;
  onCardClick?: (e: React.MouseEvent) => void;
  onPostClick?: (post: PostType) => void;
  showRepostHeader?: boolean;
  repostUserName?: string;
}

export function PostWrapper({
  post,
  isDeleted,
  children,
  onAvatarClick,
  onCardClick,
  onPostClick,
  showRepostHeader,
  repostUserName,
}: PostWrapperProps) {
  const { isCompactMode } = useCompactMode();

  // If this is an ad placeholder, render the ad component
  if (post === 'ad') {
    return <FeedAd />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.2 }}
      className={`${isDeleted ? "hidden" : ""}`}
    >
      {showRepostHeader && (
        <div className="flex items-center gap-2 text-sm text-gray-500 px-4 py-1">
          <Repeat2 className="h-4 w-4" />
          <span>{repostUserName} reposted</span>
        </div>
      )}
      <div
        className="cursor-pointer"
        onClick={(e) => {
          // Only handle post navigation if no other handler caught it
          if (onPostClick) {
            onPostClick(post);
          }
        }}
      >
        {isCompactMode ? (
          <CompactPost
            data={post}
            onLike={onCardClick}
            onPostClick={onPostClick}
          />
        ) : (
          <Card
            className="relative hover:bg-accent/50 transition-colors cursor-pointer"
          >
            <CardHeader className="flex flex-row space-x-4 items-start p-4">
              <Avatar
                className="h-10 w-10 flex-shrink-0 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent post navigation
                  if (onAvatarClick) {
                    onAvatarClick(e);
                  }
                }}
              >
                <AvatarImage src={post.image || "/default-image.png"} />
                <AvatarFallback>{post.name[0]}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col w-full">
                {children}
              </div>
            </CardHeader>
          </Card>
        )}
      </div>
    </motion.div>
  );
}